// src/component/HomeUSPBar.jsx
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

export default function USPBar() {
  const [active, setActive] = useState(null);
  const [contentIndex, setContentIndex] = useState(null);
  const [panelH, setPanelH] = useState(0);
  const [touched, setTouched] = useState(false);

  const panelRef = useRef(null);
  const innerRef = useRef(null);
  const roRef = useRef(null);
  const mountedRef = useRef(false);

  const reduceMotion = useMemo(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }, []);

  const items = [
    {
      t: "Оригинальная техника",
      s: "Гарантия производителя",
      d: (
        <>
          <p>
            Наши товары везутся через проверенных поставщиков в РФ. Часть позиций
            поставляется напрямую из Китая — от поставщиков производителя Xiaomi.
            Вся техника новая и оригинальная.
          </p>
          <div className="mt-3 text-sm md:text-base">
            Про гарантийные условия, сроки и т.д. вы можете прочитать здесь:
          </div>
          <div className="mt-2">
            <Link
              to="/warranty"
              aria-label="Перейти к разделу «Гарантийное обслуживание»"
              className="inline-flex items-center gap-1.5 rounded-lg bg-orange-50 px-3 py-1.5
                         font-semibold text-orange-600 visited:text-orange-600
                         ring-1 ring-orange-200 hover:bg-orange-100 hover:text-orange-700
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300"
            >
              Гарантийное обслуживание
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path d="M7.5 4.5a1 1 0 0 1 1.414 0l5 5a1 1 0 0 1 0 1.414l-5 5A1 1 0 1 1 7.5 14.5L11.293 10 7.5 6.207A1 1 0 0 1 7.5 4.5z" />
              </svg>
            </Link>
          </div>
        </>
      ),
    },
    {
      t: "Оплата при получении",
      s: "В магазине или у курьера",
      d: "Онлайн-оплата недоступна: у нас нет онлайн-кассы, и её применение невозможно из-за патентных ограничений на ИП. Оплатить можно при получении — в магазине или у курьера.",
    },
    {
      t: "Быстрая доставка",
      s: "По Владивостоку",
      d: "Доставка по городу Владивосток стоит 300 ₽. Доставляем в будние дни, чаще всего в день заказа. После оформления менеджер свяжется с вами, чтобы согласовать время и уточнить детали, если они не были указаны в заказе.",
    },
    {
      t: "Сервис и поддержка",
      s: "Консультации и настройка",
      d: "Наши консультанты помогут с выбором техники и ответят на вопросы. Также можем помочь с настройкой устройства — по договорённости за отдельную плату.",
    },
  ];

  const toggle = (idx) => {
    if (!touched) setTouched(true);
    if (active === idx) return closePanel();
    if (active === null) {
      setContentIndex(idx);
      setActive(idx);
      return;
    }
    setContentIndex(idx);
    setActive(idx);
  };

  const closePanel = () => {
    if (reduceMotion) {
      setActive(null);
      setContentIndex(null);
      setPanelH(0);
      return;
    }
    const current = innerRef.current?.offsetHeight || 0;
    setPanelH(current);
    requestAnimationFrame(() => setPanelH(0));
    setActive(null);
  };

  const measure = () => {
    const h = innerRef.current ? innerRef.current.scrollHeight : 0;
    setPanelH(h);
  };

  useLayoutEffect(() => {
    if (contentIndex === null) return;
    if (!mountedRef.current) mountedRef.current = true;

    const raf1 = requestAnimationFrame(() => {
      const current = panelRef.current?.offsetHeight ?? 0;
      setPanelH(current);
      const raf2 = requestAnimationFrame(() => {
        measure();
      });
      return () => cancelAnimationFrame(raf2);
    });
    return () => cancelAnimationFrame(raf1);
  }, [contentIndex]);

  useEffect(() => {
    if (!contentIndex) return;
    const schedule = () => {
      cancelAnimationFrame(schedule.rafId);
      schedule.rafId = requestAnimationFrame(measure);
    };
    if (typeof ResizeObserver !== "undefined" && innerRef.current) {
      roRef.current = new ResizeObserver(schedule);
      roRef.current.observe(innerRef.current);
    }
    const onResize = () => schedule();
    window.addEventListener("resize", onResize, { passive: true });

    return () => {
      window.removeEventListener("resize", onResize);
      if (roRef.current) {
        roRef.current.disconnect();
        roRef.current = null;
      }
      cancelAnimationFrame(schedule.rafId);
    };
  }, [contentIndex]);

  useEffect(() => {
    const el = panelRef.current;
    if (!el || reduceMotion) return;
    const onEnd = (e) => {
      if (e.propertyName !== "height") return;
      if (active === null) setContentIndex(null);
    };
    el.addEventListener("transitionend", onEnd);
    return () => el.removeEventListener("transitionend", onEnd);
  }, [active, reduceMotion]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && closePanel();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 mt-4">
      {/* Плитки — фиксируем место под иконку, чтобы ширина не «гуляла» */}
      <ul className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {items.map((i, idx) => {
          const isOpen = active === idx;
          return (
            <li key={idx} className="bg-white rounded-xl shadow">
              <button
                type="button"
                onClick={() => toggle(idx)}
                aria-expanded={isOpen}
                aria-controls="usp-panel"
                className="w-full text-left p-4 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300
                           transition-transform duration-200 ease-out"
              >
                <div className="grid grid-cols-[1fr,auto] items-start gap-3 min-w-0">
                  <div className="min-w-0">
                    <div className="text-sm md:text-base font-semibold">{i.t}</div>
                    <div className="text-xs md:text-sm text-gray-500 mt-1">{i.s}</div>
                  </div>
                  <span
                    className={`flex-none w-5 h-5 mt-0.5 text-gray-400 transition-transform duration-300 ${
                      isOpen ? "rotate-180" : ""
                    }`}
                    aria-hidden="true"
                  >
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M5.23 7.21a.75.75 0 011.06.02L10 10.17l3.71-2.94a.75.75 0 111.04 1.08l-4.24 3.36a.75.75 0 01-.94 0L5.25 8.31a.75.75 0 01-.02-1.1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </span>
                </div>
              </button>
            </li>
          );
        })}
      </ul>

      {/* Раскрывающаяся вниз панель */}
      {touched && (
        <div
          id="usp-panel"
          ref={panelRef}
          style={{
            height: reduceMotion ? "auto" : `${panelH}px`,
            willChange: reduceMotion ? "auto" : "height",
            contain: "layout",
          }}
          className={
            reduceMotion
              ? "relative overflow-hidden"
              : "relative overflow-hidden transition-[height] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]"
          }
        >
          {contentIndex !== null && (
            <div
              ref={innerRef}
              className="pt-3 px-0 transition-[opacity,transform] duration-250 ease-out opacity-100 translate-y-0"
            >
              <div className="rounded-xl border border-orange-200 bg-orange-50 px-4 py-4 sm:px-6 sm:py-5">
                <div className="text-xl md:text-2xl lg:text-3xl font-extrabold text-gray-900">
                  {items[contentIndex].t}
                </div>
                <div className="mt-1 text-sm md:text-base text-gray-500">
                  {items[contentIndex].s}
                </div>
                <div className="mt-3 text-base md:text-lg leading-relaxed text-gray-700 space-y-3">
                  {items[contentIndex].d}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
